// Angular
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// RxJS
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

// Component Library
import { FsxOption, FsxSelectComponent } from 'fsx-component-library';

// Data Contracts
import { ClerkReviewQueueAndCount } from '@data-contracts';

// Local
import { ClerkReviewFilterFormService } from '@feature/crl/clerk-review-filter-form.service';
import { ClerkReviewFilterPayloadService } from '@feature/crl/clerk-review-filter-payload.service';
import { ClerkReviewFilterStoreValuesService } from '@feature/crl/clerk-review-filter-store-values.service';

/**
 * A constant to use for the default "All Queues" option in place of magic strings.
 */
export const ALL_QUEUES_VALUE = 'ALL';

@Component({
  selector: 'app-queue-filter-control',
  standalone: true,
  imports: [ReactiveFormsModule, FsxSelectComponent, FormsModule],
  templateUrl: './queue-filter-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./queue-filter-control.component.scss'],
})
export class QueueFilterControlComponent
  implements OnInit, OnChanges, OnDestroy
{
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  private readonly formService = inject(ClerkReviewFilterFormService);
  private readonly payloadService = inject(ClerkReviewFilterPayloadService);
  private readonly storeValuesService = inject(
    ClerkReviewFilterStoreValuesService,
  );
  private readonly route = inject(ActivatedRoute);

  /**
   * The collection of objects from which we can derive a collection
   * of dropdown Option objects.
   */
  @Input() queues: ClerkReviewQueueAndCount[] = [];

  /**
   * The value of the Option that should be selected by default.
   * - Set to the "All Queues" option value when multiple queue options are available.
   * - Set to single option value when only one queue option is available.
   */
  defaultValue = ALL_QUEUES_VALUE;

  /**
   * The FormControl for the "Queue" dropdown.
   */
  formControl = this.formService.queueFormControl;

  /**
   * The collection of dropdown Option objects to pass to the FsxSelectComponent.
   */
  queueOptions: FsxOption<string>[] = [];

  /**
   * The Subject used to teardown the filter control listener subscriptions.
   */
  private destroy$$ = new Subject<void>();

  /**
   * The Angular lifecycle hook where we can safely run any code dependent on @Input() bindings.
   */
  ngOnInit(): void {
    const storedValue = this.storeValuesService.getStoredQueueValue();
    this.deriveDropdownOptions();
    this.setDefaultValue();
    // Listen first so the initial value (including a dashboard queueId) is
    // written to the query payload, not only the dropdown.
    this.startListeningForChanges();
    this.setValue(this.getInitialQueueValue(storedValue));
  }

  /**
   * Rebuilds the dropdown options when fresh queues arrive after init.
   * The filter settings service is a root singleton, so returning to the list
   * first renders the previous (stale) queue counts, then the reload emits new
   * ones. The selected value is left alone; only labels and counts refresh.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const queuesChange = changes['queues'];
    if (!queuesChange || queuesChange.firstChange) {
      return;
    }
    this.deriveDropdownOptions();
    this.setDefaultValue();
  }

  /**
   * Determines the queue value to select on load. A `queueId` query param —
   * passed by the clerk dashboard "My Pending Work" queue links — takes
   * precedence, but only when it matches a queue the user actually has;
   * otherwise the previously stored value (and, failing that, the default) is used.
   *
   * @param storedValue The queue value persisted from the user's last session.
   */
  private getInitialQueueValue(storedValue: string | null): string | null {
    const queueIdParam = this.route.snapshot.queryParamMap.get('queueId');
    const isValidQueue =
      !!queueIdParam &&
      this.queues.some(queue => queue.clerkReviewQueueId === queueIdParam);

    return isValidQueue ? queueIdParam : storedValue;
  }

  /**
   * The Angular lifecycle hook for tearing down the subscriptions with the component.
   */
  ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  /**
   * A private method to set up the FormControl valueChanges() listener.
   */
  private startListeningForChanges(): void {
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$$), distinctUntilChanged())
      .subscribe((value: string) => {
        // Use the emitted value to set the Queue filter payload.
        this.payloadService.setQueueFilter(value);
      });
  }

  /**
   * A private method to derive the dropdown Options from the passed in queues collection.
   */
  private deriveDropdownOptions(): void {
    // Derive the Queue Options from the clerkReviewFilterSettings.queues collection.
    this.queueOptions = this.queues.map((queue: ClerkReviewQueueAndCount) => {
      const option: FsxOption<string> = {
        value: queue.clerkReviewQueueId,
        label:
          queue.clerkReviewQueueName +
          ' (' +
          queue.clerkReviewQueueCount?.toString() +
          ')',
      };
      return option;
    });

    // Sort the queue options alphabetically by label
    this.queueOptions.sort((a, b) => a.label.localeCompare(b.label));

    // Conditionally Prepend the "All Queues" option.
    if (this.queueOptions.length > 1) {
      // Calculate total count from all queues
      const totalCount = this.queues.reduce((sum, queue) => {
        return sum + (queue.clerkReviewQueueCount || 0);
      }, 0);

      // Initialize the "All Queues" Option with total count
      const allQueuesOption: FsxOption<string> = {
        value: ALL_QUEUES_VALUE,
        label: `All Queues (${totalCount})`,
      };

      // Add the "All Queues" Option to the beginning of the queueOptions collection.
      this.queueOptions.unshift(allQueuesOption);
    }
  }

  /**
   * A private method to determine which option value should be used as the default value.
   */
  private setDefaultValue(): void {
    // Guard clause to prevent trying to set a value when no dropdown options were derived.
    if (this.queueOptions.length === 0) {
      console.warn('There must be at least 1 queue option to set a default.');
      return;
    }

    // Set the default value here. Always the first item in the list.
    this.defaultValue = this.queueOptions[0].value;
  }

  /**
   * A public method to update the FormControl's value.
   * - If no value is provided or if the given value is falsy, it falls back to the default value.
   *
   * @param {string | null} newValue - The new value to set.
   */
  setValue(newValue: string | null = null): void {
    const selectedValue = newValue || this.defaultValue; // Use provided value or fallback to default
    this.formControl.setValue(selectedValue); // Update FormControl with the selected value
  }
}
